import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Shield, ShieldAlert, X, AlertTriangle } from 'lucide-react';
import { getApiUrl } from '../utils/api';

const API_URL = getApiUrl();

/**
 * MM audit 3.6: Toxic-flow detector.
 *
 * Live показывает:
 * - paused/active state + обратный отсчёт до снятия pause
 * - thresholds конфига (pump/sell/big_sell)
 * - recent events (с деталями pattern)
 * - кнопка "Clear pause" (ручное снятие)
 */
const ToxicFlowCard = () => {
  const [status, setStatus] = useState(null);
  const [events, setEvents] = useState([]);
  const [clearing, setClearing] = useState(false);

  const load = async () => {
    try {
      const s = await fetch(`${API_URL}/api/market-maker/toxic-flow/status`).then(r => r.json());
      setStatus(s);
      const e = await fetch(`${API_URL}/api/market-maker/toxic-flow/events?limit=10`).then(r => r.json());
      setEvents(e.events || []);
    } catch (err) {
      // silent
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const clearPause = async () => {
    if (!window.confirm('Снять pause counter-trading?')) return;
    setClearing(true);
    try {
      const r = await fetch(`${API_URL}/api/market-maker/toxic-flow/clear-pause`, { method: 'POST' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success('Pause cleared');
      await load();
    } catch (e) {
      toast.error(`Failed: ${e.message}`);
    } finally {
      setClearing(false);
    }
  };

  const paused = !!status?.is_paused;
  const cfg = status?.config || {};
  const stats = status?.stats || {};

  return (
    <Card className={paused ? 'border-red-500/50' : ''}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          {paused ? (
            <>
              <ShieldAlert className="h-4 w-4 text-red-400" />
              Toxic Flow Detector
              <Badge className="bg-red-600 animate-pulse text-xs">
                <AlertTriangle className="h-3 w-3 mr-1" />
                PAUSED ({status?.pause_seconds_left || 0}s left)
              </Badge>
            </>
          ) : (
            <>
              <Shield className="h-4 w-4 text-emerald-400" />
              Toxic Flow Detector
              <Badge variant="outline" className="text-xs text-emerald-400 border-emerald-400/40">
                clear
              </Badge>
            </>
          )}
        </CardTitle>
        <CardDescription className="text-xs">
          Детект pump swarm ({cfg.pump?.min_buys}+ buy от {cfg.pump?.min_senders}+ senders за
          {cfg.pump?.window_sec}s, ≥${cfg.pump?.min_usd}) и mass sell
          ({cfg.mass_sell?.min_count}+ sells за {cfg.mass_sell?.window_sec}s, либо single > ${cfg.big_sell_usd}).
          При детекте → pause counter-trading {Math.round((cfg.pause_duration_sec || 0) / 60)} мин.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
          <div>
            <div className="text-xs text-slate-400">Ticks</div>
            <div className="font-bold">{stats.ticks || 0}</div>
          </div>
          <div>
            <div className="text-xs text-slate-400">Events total</div>
            <div className="font-bold">{stats.events_total || 0}</div>
          </div>
          <div>
            <div className="text-xs text-slate-400">Pauses triggered</div>
            <div className="font-bold">{stats.pauses_triggered || 0}</div>
          </div>
          <div>
            <div className="text-xs text-slate-400">Last pattern</div>
            <div className="font-bold text-xs">{stats.last_event || '—'}</div>
          </div>
        </div>

        {paused && (
          <div className="mb-3">
            <Button onClick={clearPause} disabled={clearing} variant="destructive" size="sm">
              <X className="h-4 w-4 mr-1" />
              {clearing ? 'Clearing...' : 'Clear pause (admin override)'}
            </Button>
            <p className="text-xs text-slate-500 mt-1">
              Until: {status?.pause_until?.slice(0, 19)} UTC
            </p>
          </div>
        )}

        {events.length > 0 ? (
          <details open={paused}>
            <summary className="cursor-pointer text-xs text-slate-400 hover:text-slate-200">
              Recent events ({events.length})
            </summary>
            <table className="mt-2 w-full text-xs">
              <thead>
                <tr className="text-slate-500">
                  <th className="text-left">Detected</th>
                  <th>Pattern</th>
                  <th>Until</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e, i) => (
                  <tr key={i} className="border-t border-slate-800">
                    <td className="text-slate-400">{(e.detected_at || '').slice(0, 19)}</td>
                    <td className="text-red-400">{e.pattern}</td>
                    <td className="text-slate-500">{(e.pause_until || '').slice(0, 19)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        ) : (
          <div className="text-xs text-slate-500">No toxic events detected yet.</div>
        )}
      </CardContent>
    </Card>
  );
};

export default ToxicFlowCard;
